import { Injectable } from '@angular/core';
import { RestService } from './rest.service';

export interface MaccabiPatientListItem {
  linkId?: number;
  guid: string | null;
  patientUserId?: number;
  name: string;
  email?: string;
  idNumber?: string | null;
  idLast4?: string | null;
  status: 'updated' | 'stale' | 'pending';
  scanCount?: number;
  lastScanAt?: string;
  age?: number;
  hasReport?: boolean;
  source: 'approved' | 'maccabi';
}

export interface MaccabiPendingInvite {
  inviteId: number;
  phone: string;
  phoneMasked: string;
  patientEmail?: string;
  idLast4?: string;
  created: string;
  expiresAt: string;
  expired?: boolean;
  smsStatus?: string;
  inviteUrl?: string;
}

export interface MaccabiPatientUpdateEntry {
  section: 'sleep' | 'mood' | 'physical';
  question: string;
  mode: 'voice' | 'written';
  text: string;
  transcript: string;
  hasAudio: boolean;
}

export interface MaccabiPatientUpdate {
  patientUserId: number;
  patientName: string;
  patientIdMasked: string;
  created: string;
  sections: string[];
  quote: string;
  entries: MaccabiPatientUpdateEntry[];
}

export interface MaccabiRealtimeToken {
  token: string;
  docId: string;
}

@Injectable({ providedIn: 'root' })
export class MaccabiService {
  constructor(private restService: RestService) {}

  startTwoFactor(phone: string, sendSms = false): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: 'maccabi/2fa/start',
      body: { phone, sendSms },
      throwError: true,
    }) as Promise<any>;
  }

  verifyTwoFactor(challengeId: number, code: string): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: 'maccabi/2fa/verify',
      body: { challengeId, code },
      throwError: true,
    }) as Promise<any>;
  }

  startRegistrationPhoneValidation(phone: string, sendSms = false): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: 'maccabi/registration/phone/start',
      body: { phone, sendSms },
      throwError: true,
    }) as Promise<any>;
  }

  verifyRegistrationPhone(challengeId: number, phone: string, code: string): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: 'maccabi/registration/phone/verify',
      body: { challengeId, phone, code },
      throwError: true,
    }) as Promise<any>;
  }

  createDoctorAccount(payload: {
    fullName: string;
    email: string;
    password: string;
    brand?: string;
    lang?: string;
    clinicSlug?: string | null;
    memberInviteToken?: string | null;
  }): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: 'maccabi/registration/doctor/create',
      body: payload,
      throwError: true,
    }) as Promise<any>;
  }

  getPatients(clinic?: string | null): Promise<{ patients: MaccabiPatientListItem[]; pendingInvites: MaccabiPendingInvite[]; verified?: boolean; clinics?: any[] }> {
    return this.restService.request({
      method: 'GET',
      path: clinic ? `maccabi/patients?clinic=${encodeURIComponent(clinic)}` : 'maccabi/patients',
      throwError: true,
    }) as Promise<{ patients: MaccabiPatientListItem[]; pendingInvites: MaccabiPendingInvite[]; verified?: boolean; clinics?: any[] }>;
  }

  // Stage 5: connected patients' self-reports since the last visit, in arrival
  // order, verbatim (no severity ranking).
  getPatientUpdates(): Promise<{ updates: MaccabiPatientUpdate[] }> {
    return this.restService.request({
      method: 'GET',
      path: 'maccabi/updates',
      throwError: true,
    }) as Promise<{ updates: MaccabiPatientUpdate[] }>;
  }

  // Doctor detaches one of her patients (her access ends; the patient keeps her data).
  detachPatient(linkId: number): Promise<any> {
    return this.restService.request({
      method: 'DELETE',
      path: `maccabi/patients/${linkId}`,
      throwError: true,
    }) as Promise<any>;
  }

  getRealtimeToken(): Promise<MaccabiRealtimeToken> {
    return this.restService.request({
      method: 'GET',
      path: 'maccabi/realtime-token',
      throwError: true,
    }) as Promise<MaccabiRealtimeToken>;
  }

  createInvitation(
    phone: string,
    patientEmail: string,
    dob?: string,
    sendSms = false,
    clinic?: string | null,
  ): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: 'maccabi/invitations',
      body: { phone, patientEmail, dob, sendSms, clinic: clinic || undefined },
      throwError: true,
    }) as Promise<any>;
  }

  remindInvitation(inviteId: number, sendSms = false): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: `maccabi/invitations/${inviteId}/remind`,
      body: { sendSms },
      throwError: true,
    }) as Promise<any>;
  }

  regenerateInvitation(inviteId: number, sendSms = false): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: `maccabi/invitations/${inviteId}/regenerate`,
      body: { sendSms },
      throwError: true,
    }) as Promise<any>;
  }

  getInvitation(token: string): Promise<any> {
    return this.restService.request({
      method: 'GET',
      path: `maccabi/invitations/${token}`,
      throwError: true,
    }) as Promise<any>;
  }

  approveInvitation(token: string): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: `maccabi/invitations/${token}/approve`,
      throwError: true,
    }) as Promise<any>;
  }

  // The doctor's reusable personal invite link (create-or-get), /i/<token>.
  getInviteLink(clinic?: string | null): Promise<{ inviteUrl: string; token: string }> {
    return this.restService.request({
      method: 'GET',
      path: clinic ? `maccabi/me/invite-link?clinic=${encodeURIComponent(clinic)}` : 'maccabi/me/invite-link',
      throwError: true,
    }) as Promise<{ inviteUrl: string; token: string }>;
  }

  // Save the doctor's optional clinic/practice name + professional credential
  // (setup wizard step 1).
  saveProfile(clinic: string, credential = ''): Promise<any> {
    return this.restService.request({
      method: 'POST',
      path: 'maccabi/me/profile',
      body: { clinic, credential },
      throwError: true,
    }) as Promise<any>;
  }

  getMyDoctors(): Promise<{ doctors: any[] }> {
    return this.restService.request({
      method: 'GET',
      path: 'maccabi/me/doctors',
      throwError: true,
    }) as Promise<{ doctors: any[] }>;
  }

  removeDoctor(linkId: number): Promise<any> {
    return this.restService.request({
      method: 'DELETE',
      path: `maccabi/me/doctors/${linkId}`,
      throwError: true,
    }) as Promise<any>;
  }

  getReportViewData(scanId: string): Promise<any> {
    return this.restService.request({
      method: 'GET',
      path: `maccabi/report/${scanId}/view-data`,
      throwError: true,
    }) as Promise<any>;
  }

  async downloadPdf(scanId: string): Promise<Blob> {
    const token = this.restService['sessionService'].session?.token || '';
    const baseUrl = this.restService['baseUrl'];
    const clientUrl = window.location.href;
    const url = new URL(`maccabi/report/${scanId}/pdf`, baseUrl).toString()
      + `?clientUrl=${encodeURIComponent(clientUrl)}`;

    const response = await fetch(url, {
      headers: { Authorization: token },
    });
    if (!response.ok) {
      throw new Error(`Maccabi PDF generation failed: ${response.status}`);
    }
    return response.blob();
  }
}
